import React, { useState, useEffect, useRef } from 'react';
import {
  Shield, Globe, Map, User, Link as LinkIcon, AlertTriangle, Search, X, ExternalLink,
  ShieldAlert, Database, Clock, Archive, Camera, Film, Hash, FileCheck, Building2,
  Flag, Navigation, Plane, Ship, MapPin, Anchor, Brain, Wand2, Mic, Fingerprint,
  TrendingUp, Activity, RefreshCw, Download, Share2, Layers,
  ChevronDown, ChevronRight, BarChart2, Zap, Radio, Network, Info, CheckCircle,
  FileText, Eye, GitBranch, Briefcase,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { API_BASE_URL } from '../config';
import type { MonitoringScope } from '../types';
import type { IntelligenceFinding } from '../services/apiService';

interface IntelligenceCenterProps {
  monitoringScope?: MonitoringScope;
}

interface OSINTFinding extends IntelligenceFinding {
  entity: string;
  type: 'IP' | 'Domain' | 'Email' | 'Phone' | 'Business' | 'Sanctioned' | 'ArchivedURL' | 'EXIFData' | 'DeepfakeScan' | 'EvidenceCapture' | 'GeoLocation' | 'FlightTrack' | 'VesselTrack' | 'CrisisZone' | 'AIGeneratedImage' | 'LLMText' | 'VoiceClone' | 'NeuralWatermark';
  suite: 'cyber' | 'identity' | 'compliance' | 'media' | 'geo' | 'neural';
  reputation: string;
  risk_score: number;
  source: string;
  timestamp: string;
  associated_post: string;
  country?: string;
  abuse_reports?: number;
  related_posts?: string[];
  recommendations?: string[];
  impact_context?: string;
  correlation_tags?: string[];
  narrative_footprint?: { narrative: string; exposure: number; platforms: string[] }[];
  velocity_projection?: { impressions_48h: number; viral_probability: number; peak_hour: string };
  risk_breakdown?: { label: string; pct: number; color: string }[];
  company_number?: string;
  jurisdiction?: string;
  sanctions_list?: string;
  archive_url?: string;
  archive_timestamp?: string;
  sha256_hash?: string;
  media_url?: string;
  deepfake_score?: number;
  exif_metadata?: Record<string, string>;
  lat?: number;
  lon?: number;
  resolved_location?: string;
  crisis_zone?: string;
  crisis_severity?: 'critical' | 'high' | 'medium';
  distance_km?: number;
  callsign?: string;
  flight_origin?: string;
  altitude_m?: number;
  velocity_ms?: number;
  on_ground?: boolean;
  vessel_name?: string;
  vessel_type?: string;
  speed_kn?: number;
  destination?: string;
  mmsi?: string;
  ai_confidence?: number;
  generation_method?: string;
  model_attribution?: string;
  perplexity_score?: number;
  burstiness_score?: number;
  watermark_status?: 'present' | 'absent' | 'tampered';
  c2pa_issuer?: string;
  frequency_anomaly?: boolean;
  formant_deviation?: number;
}

interface ArchiveSummary {
  id: string;
  timestamp: string;
  findings_count: number;
  high_risk_count: number;
  title: string;
  filename: string;
  data: string;
}

const SWEEP_PHASES = [
  'Scanning OSINT signal sources...',
  'Mapping entity relationships...',
  'Running AI narrative classification...',
  'Enriching geo & media forensics...',
  'Cross-referencing sanctions & OFAC databases...',
  'Compiling threat narratives & finalising graph...',
];

const riskColor  = (s: number) => s > 0.8 ? 'text-rose-400'   : s > 0.6 ? 'text-amber-400'  : 'text-emerald-400';
const riskBg     = (s: number) => s > 0.8 ? 'bg-rose-500/10 border-rose-500/20' : s > 0.6 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20';
const riskBar    = (s: number) => s > 0.8 ? 'bg-rose-500'     : s > 0.6 ? 'bg-amber-500'    : 'bg-emerald-500';

const typeStyle = (type: OSINTFinding['type']): string => ({
  IP: 'bg-blue-500/10 text-blue-400', Domain: 'bg-green-500/10 text-green-400',
  Email: 'bg-indigo-500/10 text-indigo-400', Phone: 'bg-purple-500/10 text-purple-400',
  Business: 'bg-amber-500/10 text-amber-400', Sanctioned: 'bg-rose-500/10 text-rose-400',
  ArchivedURL: 'bg-cyan-500/10 text-cyan-400', EXIFData: 'bg-teal-500/10 text-teal-400',
  DeepfakeScan: 'bg-fuchsia-500/10 text-fuchsia-400', EvidenceCapture: 'bg-lime-500/10 text-lime-400',
  GeoLocation: 'bg-emerald-500/10 text-emerald-400', FlightTrack: 'bg-sky-500/10 text-sky-400',
  VesselTrack: 'bg-teal-500/10 text-teal-400', CrisisZone: 'bg-red-500/10 text-red-400',
  AIGeneratedImage: 'bg-violet-500/10 text-violet-400', LLMText: 'bg-purple-500/10 text-purple-400',
  VoiceClone: 'bg-pink-500/10 text-pink-400', NeuralWatermark: 'bg-indigo-500/10 text-indigo-400',
}[type]);

const suiteBadge = (s: string) => ({
  cyber:      'bg-blue-500/10 text-blue-400 border-blue-500/20',
  identity:   'bg-purple-500/10 text-purple-400 border-purple-500/20',
  compliance: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  media:      'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20',
  geo:        'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  neural:     'bg-violet-500/10 text-violet-400 border-violet-500/20',
}[s] ?? 'bg-slate-500/10 text-slate-400 border-slate-500/20');

const suiteLabel = (s: string) => ({
  cyber: '1 \u00b7 Cyber', identity: '2 \u00b7 Identity', compliance: '3 \u00b7 Compliance',
  media: '4 \u00b7 Media', geo: '5 \u00b7 Geo & Transport', neural: '6 \u00b7 Neural AI Forensics',
}[s] ?? s);

const suiteNum = (s: string) => ({ cyber:'1', identity:'2', compliance:'3', media:'4', geo:'5', neural:'6' }[s] ?? '?');

const typeIcon = (type: OSINTFinding['type']): React.ReactNode => ({
  IP: <Map className="w-5 h-5" />, Domain: <Globe className="w-5 h-5" />,
  Email: <LinkIcon className="w-5 h-5" />, Phone: <User className="w-5 h-5" />,
  Business: <Building2 className="w-5 h-5" />, Sanctioned: <Flag className="w-5 h-5" />,
  ArchivedURL: <Archive className="w-5 h-5" />, EXIFData: <Camera className="w-5 h-5" />,
  DeepfakeScan: <Film className="w-5 h-5" />, EvidenceCapture: <FileCheck className="w-5 h-5" />,
  GeoLocation: <MapPin className="w-5 h-5" />, FlightTrack: <Plane className="w-5 h-5" />,
  VesselTrack: <Ship className="w-5 h-5" />, CrisisZone: <Navigation className="w-5 h-5" />,
  AIGeneratedImage: <Wand2 className="w-5 h-5" />, LLMText: <Brain className="w-5 h-5" />,
  VoiceClone: <Mic className="w-5 h-5" />, NeuralWatermark: <Fingerprint className="w-5 h-5" />,
}[type]);

const MOCK_FINDINGS: OSINTFinding[] = [
  {
    entity: '192.168.4.152', type: 'IP', suite: 'cyber',
    reputation: 'Reported for SSH Brute Force (AbuseIPDB)',
    impact_context: 'SSH Brute Force (AbuseIPDB) \u2013 Linked to 3 recent fact-check domain attacks',
    correlation_tags: ['Linked: factcheck-news.tk', 'Same /24 subnet as 3 known C2 nodes'],
    risk_breakdown: [{ label: 'Abuse signal density', pct: 42, color: 'bg-rose-500' }, { label: 'C2 infrastructure overlap', pct: 28, color: 'bg-orange-500' }, { label: 'Geo-risk (RU/TOR exit)', pct: 15, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'Coordinated election interference narrative', exposure: 0.82, platforms: ['Twitter', 'Telegram'] }, { narrative: 'Fact-checker silencing campaign', exposure: 0.64, platforms: ['Reddit', 'Mastodon'] }],
    velocity_projection: { impressions_48h: 1200000, viral_probability: 0.67, peak_hour: '2026-05-31T22:00:00' },
    risk_score: 0.85, source: 'AbuseIPDB', timestamp: new Date().toISOString(), associated_post: 'tweet_12847',
    country: 'Russia (RU)', abuse_reports: 47, related_posts: ['tweet_12847', 'tweet_12901', 'reddit_4455'],
    recommendations: ['Block at firewall level', 'Report to AbuseIPDB', 'Trace affiliated accounts', 'Escalate to CERT'],
  },
  {
    entity: 'factcheck-news.tk', type: 'Domain', suite: 'cyber',
    reputation: 'Malicious domain hosted on bulletproof server',
    impact_context: 'Bulletproof host + active disinformation cluster \u2013 mirrors 7 suspended fact-check domains',
    correlation_tags: ['Linked: TruthMedia Holdings Ltd.', 'Same registrar as 14 suspended misinfo domains'],
    risk_breakdown: [{ label: 'Domain reputation score', pct: 48, color: 'bg-rose-500' }, { label: 'Hosting infrastructure risk', pct: 27, color: 'bg-orange-500' }, { label: 'Disinformation cluster overlap', pct: 17, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'Anti-fact-checker network', exposure: 0.91, platforms: ['Reddit', 'Twitter', 'Mastodon'] }],
    velocity_projection: { impressions_48h: 2800000, viral_probability: 0.84, peak_hour: '2026-05-31T20:00:00' },
    risk_score: 0.92, source: 'AlienVault OTX', timestamp: new Date().toISOString(), associated_post: 'reddit_9921',
    country: 'Netherlands (NL)', abuse_reports: 129, related_posts: ['reddit_9921', 'tweet_10042', 'mastodon_882'],
    recommendations: ['Submit for domain takedown', 'Alert registrar', 'Add to blocklist', 'Cross-reference with known disinformation networks'],
  },
  {
    entity: '+1 (555) 019-2831', type: 'Phone', suite: 'identity',
    reputation: 'VoIP Number (Twilio Lookup) \u2014 high-risk carrier',
    impact_context: 'VoIP (Twilio) \u2013 High-risk carrier used in 11 verified influence ops across 3 regions',
    correlation_tags: ['Linked: Igor Petrov campaign (2025)', 'Carrier flagged in 4 OSINT reports'],
    risk_breakdown: [{ label: 'Carrier risk rating', pct: 38, color: 'bg-amber-500' }, { label: 'Influence op association', pct: 22, color: 'bg-rose-500' }, { label: 'Account velocity anomaly', pct: 15, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Coordinated bot amplification', exposure: 0.71, platforms: ['Twitter', 'Facebook'] }],
    velocity_projection: { impressions_48h: 540000, viral_probability: 0.41, peak_hour: '2026-06-01T08:00:00' },
    risk_score: 0.75, source: 'Twilio', timestamp: new Date().toISOString(), associated_post: 'tweet_12847',
    country: 'United States (US)', abuse_reports: 11, related_posts: ['tweet_12847', 'facebook_3891'],
    recommendations: ['Flag for carrier review', 'Monitor associated accounts', 'Cross-reference with bot network data'],
  },
  {
    entity: 'TruthMedia Holdings Ltd.', type: 'Business', suite: 'compliance',
    reputation: 'Registered shell company linked to disinformation website network',
    impact_context: 'Shell company \u2192 disinformation network (78% risk) \u2013 BVI jurisdiction obscures beneficial ownership',
    correlation_tags: ['Linked: factcheck-news.tk', 'Linked: Igor Petrov (board member)'],
    risk_breakdown: [{ label: 'Shell company opacity', pct: 35, color: 'bg-rose-500' }, { label: 'Disinformation network overlap', pct: 22, color: 'bg-orange-500' }, { label: 'Jurisdiction risk (BVI)', pct: 21, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'Coordinated influence infrastructure', exposure: 0.78, platforms: ['Multiple'] }],
    velocity_projection: { impressions_48h: 1800000, viral_probability: 0.62, peak_hour: '2026-05-31T18:00:00' },
    risk_score: 0.78, source: 'OpenCorporates', timestamp: new Date().toISOString(), associated_post: 'reddit_9921',
    country: 'British Virgin Islands (VG)', company_number: 'BVI-2024-00182', jurisdiction: 'British Virgin Islands',
    related_posts: ['reddit_9921', 'tweet_10042'],
    recommendations: ['Report to financial intelligence unit', 'Flag beneficial owner for sanctions check', 'Submit to OpenCorporates watchlist'],
  },
  {
    entity: 'Igor Petrov', type: 'Sanctioned', suite: 'compliance',
    reputation: 'OFAC SDN List \u2014 sanctioned for coordinated influence operations',
    impact_context: 'Sanctioned actor \u2013 Coordinated influence operations (97% risk) across OFAC, EU, UN lists',
    correlation_tags: ['Linked: TruthMedia Holdings Ltd.', 'Linked: pm_emergency_broadcast_audio.mp3'],
    risk_breakdown: [{ label: 'Sanctions exposure', pct: 51, color: 'bg-rose-500' }, { label: 'Active influence op footprint', pct: 28, color: 'bg-orange-500' }, { label: 'Cross-border coordination', pct: 18, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'State-sponsored disinformation (Russia)', exposure: 0.97, platforms: ['Telegram', 'Twitter', 'RT'] }, { narrative: 'Election interference 2026', exposure: 0.89, platforms: ['Facebook', 'Telegram'] }],
    velocity_projection: { impressions_48h: 5200000, viral_probability: 0.93, peak_hour: '2026-05-31T21:00:00' },
    risk_score: 0.97, source: 'OpenSanctions', timestamp: new Date().toISOString(), associated_post: 'tweet_13201',
    country: 'Russia (RU)', sanctions_list: 'OFAC SDN, EU Sanctions, UN Security Council',
    related_posts: ['tweet_13201', 'telegram_0091'],
    recommendations: ['Escalate to legal team immediately', 'Block all associated accounts', 'Report to national authorities', 'Document all affiliated infrastructure'],
  },
  {
    entity: 'https://election-truth.news/bombshell-video', type: 'ArchivedURL', suite: 'media',
    reputation: 'Wayback Machine snapshot secured \u2014 article since deleted',
    impact_context: 'Deleted election article \u2013 Wayback snapshot secured as legal evidence (deleted 4h after posting)',
    correlation_tags: ['Linked: factcheck-news.tk registrar', 'Article shared 14,200 times before deletion'],
    risk_breakdown: [{ label: 'Deletion intent probability', pct: 44, color: 'bg-amber-500' }, { label: 'Domain disinformation score', pct: 27, color: 'bg-rose-500' }],
    narrative_footprint: [{ narrative: 'Election fraud conspiracy', exposure: 0.71, platforms: ['Reddit', 'Twitter'] }],
    velocity_projection: { impressions_48h: 890000, viral_probability: 0.58, peak_hour: '2026-05-31T15:00:00' },
    risk_score: 0.71, source: 'Archive.org', timestamp: new Date().toISOString(), associated_post: 'tweet_14002',
    archive_url: 'https://web.archive.org/web/20260430121003/https://election-truth.news/bombshell-video',
    archive_timestamp: '2026-04-30T12:10:03Z',
    related_posts: ['tweet_14002', 'reddit_5512'],
    recommendations: ['Preserve archive URL as legal evidence', 'Notify platform trust & safety team', 'Cross-reference with known disinformation domains'],
  },
  {
    entity: 'protest_crowd_photo.jpg', type: 'EXIFData', suite: 'media',
    reputation: 'Image metadata mismatch \u2014 GPS coordinates inconsistent with claimed location',
    impact_context: 'Metadata tampering probability: 68% \u2013 GPS shows Delhi, caption claims London protest (Nov 2024)',
    correlation_tags: ['Linked: activist_march_kyiv_2026.jpg (same editing software)', 'EXIF + Media Forensics cross-match'],
    risk_breakdown: [{ label: 'GPS location mismatch', pct: 38, color: 'bg-amber-500' }, { label: 'Editing software anomaly', pct: 30, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Protest misrepresentation', exposure: 0.68, platforms: ['Facebook', 'Twitter'] }],
    velocity_projection: { impressions_48h: 340000, viral_probability: 0.45, peak_hour: '2026-05-31T14:00:00' },
    risk_score: 0.82, source: 'EXIF Extractor', timestamp: new Date().toISOString(), associated_post: 'facebook_7721',
    media_url: 'https://cdn.example.com/uploads/protest_crowd_photo.jpg',
    exif_metadata: { 'Camera Make': 'Samsung Galaxy S20', 'GPS Latitude': '28.6139\u00b0 N (Delhi, India)', 'GPS Longitude': '77.2090\u00b0 E', 'Date Taken': '2024-11-15 14:22:07', 'Software': 'Adobe Photoshop 25.3', 'Original Filename': 'img_2024_1115_protest_UK.jpg' },
    related_posts: ['facebook_7721', 'twitter_8833'],
    recommendations: ['Flag image as manipulated/mislabeled', 'Submit GPS mismatch as evidence', 'Run reverse image search', 'Notify fact-checking partners'],
  },
  {
    entity: 'minister_speech_clip.mp4', type: 'DeepfakeScan', suite: 'media',
    reputation: 'High probability deepfake \u2014 facial inconsistencies detected',
    impact_context: '2 probable deepfakes detected \u2013 Facial rendering artifacts at 94% confidence, ElevenLabs audio track',
    correlation_tags: ['Linked: pm_emergency_broadcast_audio.mp3 (same operator)', 'Neural AI Forensics: 2 deepfakes'],
    risk_breakdown: [{ label: 'Facial artifact score', pct: 52, color: 'bg-rose-500' }, { label: 'Audio-visual sync anomaly', pct: 26, color: 'bg-orange-500' }, { label: 'Known model signature', pct: 16, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'Government impersonation', exposure: 0.94, platforms: ['Telegram', 'YouTube'] }],
    velocity_projection: { impressions_48h: 4100000, viral_probability: 0.91, peak_hour: '2026-05-31T19:00:00' },
    risk_score: 0.94, source: 'Deepfake Detector (Local)', timestamp: new Date().toISOString(), associated_post: 'telegram_0091',
    media_url: 'https://cdn.example.com/uploads/minister_speech_clip.mp4', deepfake_score: 0.94,
    related_posts: ['telegram_0091', 'youtube_2241'],
    recommendations: ['Mark content as synthetic media', 'Request platform emergency removal', 'Alert subject of deepfake', 'Forward to digital forensics unit'],
  },
  {
    entity: 'leaked_document_scan.pdf', type: 'EvidenceCapture', suite: 'media',
    reputation: 'Evidence secured with cryptographic hash \u2014 chain of custody established',
    impact_context: 'Chain of custody secured \u2013 SHA-256 hash locked in immutable evidence log (reddit_6601)',
    correlation_tags: ['Chain of custody: Active', 'Legal hold: Requested'],
    risk_breakdown: [{ label: 'Document authenticity risk', pct: 35, color: 'bg-amber-500' }, { label: 'Disinformation template match', pct: 30, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Leaked intelligence documents', exposure: 0.65, platforms: ['Reddit'] }],
    velocity_projection: { impressions_48h: 210000, viral_probability: 0.38, peak_hour: '2026-06-01T10:00:00' },
    risk_score: 0.65, source: 'Evidence Capture Engine', timestamp: new Date().toISOString(), associated_post: 'reddit_6601',
    sha256_hash: 'a3f1c7b2e9d4082f6a1e3c5b9f2d7a0e4c8b1f3e7a2d5c9b0f4e8a1c6d3b7f2e',
    related_posts: ['reddit_6601'],
    recommendations: ['Store hash in immutable evidence log', 'Submit to legal team for chain of custody', 'Cross-reference with known disinformation document templates'],
  },
  {
    entity: 'Kyiv, Ukraine', type: 'GeoLocation', suite: 'geo',
    reputation: 'Place mention resolved to coordinates \u2014 within Ukraine Conflict Zone radius (312 km)',
    impact_context: 'Crisis zone coordinates confirmed \u2013 312 km from Ukraine conflict perimeter, elevated monitoring',
    correlation_tags: ['Crisis Zone: Ukraine Conflict', 'Linked: RYR4471 flight track (same coordinates)'],
    risk_breakdown: [{ label: 'Crisis proximity risk', pct: 55, color: 'bg-rose-500' }, { label: 'Misinfo amplification zone', pct: 33, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Ukraine conflict misinformation', exposure: 0.88, platforms: ['Twitter', 'Telegram'] }],
    velocity_projection: { impressions_48h: 3200000, viral_probability: 0.79, peak_hour: '2026-05-31T20:00:00' },
    risk_score: 0.88, source: 'Nominatim / OpenStreetMap', timestamp: new Date().toISOString(), associated_post: 'tweet_15001',
    lat: 50.4501, lon: 30.5234, resolved_location: 'Kyiv, Kyiv city, Ukraine',
    crisis_zone: 'Ukraine Conflict Zone', crisis_severity: 'critical', distance_km: 312.4,
    related_posts: ['tweet_15001', 'telegram_1102'],
    recommendations: ['Cross-reference with conflict map overlays', 'Escalate to geopolitical analyst', 'Monitor for troop movement narratives', 'Archive surrounding posts'],
  },
  {
    entity: 'RYR4471 \u2014 Ryanair 4471', type: 'FlightTrack', suite: 'geo',
    reputation: 'Aircraft detected near crisis coordinates \u2014 emergency squawk 7700 observed nearby',
    impact_context: 'Emergency squawk 7700 near crisis zone \u2013 ADS-B anomaly logged, route deviation flagged',
    correlation_tags: ['Linked: Kyiv crisis zone', 'NOTAM active in corridor'],
    risk_breakdown: [{ label: 'Route deviation anomaly', pct: 40, color: 'bg-amber-500' }, { label: 'Crisis proximity score', pct: 33, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Aviation incident misreporting', exposure: 0.55, platforms: ['Twitter'] }],
    velocity_projection: { impressions_48h: 180000, viral_probability: 0.33, peak_hour: '2026-06-01T06:00:00' },
    risk_score: 0.73, source: 'OpenSky Network', timestamp: new Date().toISOString(), associated_post: 'tweet_15001',
    callsign: 'RYR4471', flight_origin: 'Ireland', lat: 50.8921, lon: 29.8143, altitude_m: 10972, velocity_ms: 230.5, on_ground: false,
    related_posts: ['tweet_15001'],
    recommendations: ['Monitor ADS-B feed for route deviation', 'Cross-check with NOTAM databases', 'Alert aviation authorities if near no-fly zone'],
  },
  {
    entity: 'PACIFIC HORIZON (MMSI: 636019263)', type: 'VesselTrack', suite: 'geo',
    reputation: 'Cargo vessel on approach vector through monitored maritime corridor',
    impact_context: 'Sanctions cross-check pending \u2013 Liberian-flagged bulk carrier, cargo manifest not declared',
    correlation_tags: ['Sanctions list cross-check: In Progress', 'AIS signal gap: 4h 22m (suspicious)'],
    risk_breakdown: [{ label: 'Sanctions overlap risk', pct: 32, color: 'bg-amber-500' }, { label: 'AIS signal anomaly', pct: 29, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Illicit cargo disinformation', exposure: 0.42, platforms: ['Reddit'] }],
    velocity_projection: { impressions_48h: 90000, viral_probability: 0.21, peak_hour: '2026-06-01T12:00:00' },
    risk_score: 0.61, source: 'AIS / MarineTraffic', timestamp: new Date().toISOString(), associated_post: 'reddit_7801',
    vessel_name: 'PACIFIC HORIZON', vessel_type: 'Bulk Carrier', mmsi: '636019263', lat: 50.7802, lon: 29.6540, speed_kn: 14.2, destination: 'ROTTERDAM', country: 'Liberia (LR)',
    related_posts: ['reddit_7801'],
    recommendations: ['Flag for maritime intelligence review', 'Cross-check cargo manifest against sanctions list', 'Notify coast guard if route anomaly detected'],
  },
  {
    entity: 'Gaza Strip Conflict Zone \u2014 31.5\u00b0N 34.46\u00b0E', type: 'CrisisZone', suite: 'geo',
    reputation: 'Post-extracted IP resolved 14 km inside active conflict zone perimeter',
    impact_context: 'CRITICAL zone \u2013 IP origin 14 km inside active conflict perimeter, treat as combat-zone misinfo',
    correlation_tags: ['Severity: CRITICAL', 'Humanitarian intelligence escalation required'],
    risk_breakdown: [{ label: 'Conflict zone proximity', pct: 58, color: 'bg-rose-500' }, { label: 'Misinfo amplification risk', pct: 25, color: 'bg-orange-500' }, { label: 'Attribution uncertainty', pct: 12, color: 'bg-amber-500' }],
    narrative_footprint: [{ narrative: 'Gaza conflict casualty misreporting', exposure: 0.95, platforms: ['Twitter', 'Telegram', 'Facebook'] }],
    velocity_projection: { impressions_48h: 7800000, viral_probability: 0.97, peak_hour: '2026-05-31T23:00:00' },
    risk_score: 0.95, source: 'ip-api.com + Crisis Zone KB', timestamp: new Date().toISOString(), associated_post: 'facebook_9102',
    lat: 31.5000, lon: 34.4667, crisis_zone: 'Gaza Strip Conflict', crisis_severity: 'critical', distance_km: 14.2,
    related_posts: ['facebook_9102', 'telegram_2201'],
    recommendations: ['Treat content as potential combat zone misinformation', 'Escalate to humanitarian intelligence desk', 'Archive all associated media immediately', 'Notify regional analyst team'],
  },
  {
    entity: 'activist_march_kyiv_2026.jpg', type: 'AIGeneratedImage', suite: 'neural',
    reputation: 'AI-generated protest crowd image \u2014 Stable Diffusion XL fingerprint confirmed (91% confidence)',
    impact_context: 'Probable deepfake detected \u2013 Stable Diffusion XL fingerprint, protest crowd fabricated at 91%',
    correlation_tags: ['Linked: protest_crowd_photo.jpg (same editing pattern)', 'Neural AI Forensics: 2 deepfakes'],
    risk_breakdown: [{ label: 'AI generation confidence', pct: 51, color: 'bg-violet-500' }, { label: 'Narrative manipulation intent', pct: 25, color: 'bg-rose-500' }, { label: 'Distribution velocity', pct: 15, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Fabricated protest coverage', exposure: 0.91, platforms: ['Twitter', 'Telegram', 'Reddit'] }],
    velocity_projection: { impressions_48h: 2900000, viral_probability: 0.88, peak_hour: '2026-05-31T22:00:00' },
    risk_score: 0.91, source: 'Neural Flow / Hive AI', timestamp: new Date().toISOString(), associated_post: 'tweet_16001',
    media_url: 'https://cdn.example.com/uploads/activist_march_kyiv_2026.jpg',
    ai_confidence: 0.91, generation_method: 'Diffusion Model (high confidence)', model_attribution: 'Stable Diffusion XL',
    related_posts: ['tweet_16001', 'telegram_3301', 'reddit_9910'],
    recommendations: ['Label as AI-generated synthetic media', 'Report to platform trust & safety', 'Cross-reference with known Midjourney/SDXL image databases', 'Alert fact-checking partners immediately'],
  },
  {
    entity: 'BREAKING: Major election fraud uncovered in 7 swing states\u2026', type: 'LLMText', suite: 'neural',
    reputation: 'News article classified as AI-generated \u2014 GPT-4 signature detected, perplexity score 21.4',
    impact_context: 'GPT-4 signature detected \u2013 Perplexity 21.4 (AI threshold: <30), burstiness 0.18, mass-posted',
    correlation_tags: ['Linked: activist_march_kyiv_2026.jpg (same campaign)', 'LLM mass-posting pattern detected'],
    risk_breakdown: [{ label: 'LLM generation confidence', pct: 44, color: 'bg-purple-500' }, { label: 'Narrative harm potential', pct: 28, color: 'bg-rose-500' }, { label: 'Mass-posting velocity', pct: 15, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Election fraud conspiracy (AI-generated)', exposure: 0.87, platforms: ['Reddit', 'Twitter', 'Facebook'] }],
    velocity_projection: { impressions_48h: 3600000, viral_probability: 0.82, peak_hour: '2026-05-31T20:00:00' },
    risk_score: 0.87, source: 'Neural Flow / Sapling AI', timestamp: new Date().toISOString(), associated_post: 'reddit_8801',
    ai_confidence: 0.87, generation_method: 'LLM', model_attribution: 'GPT-4 / Claude (heuristic)', perplexity_score: 21.4, burstiness_score: 0.18,
    related_posts: ['reddit_8801', 'tweet_17002', 'facebook_5512'],
    recommendations: ['Flag for AI-generated content disclosure', 'Prioritise human fact-checker review', 'Check publishing account for LLM mass-posting pattern', 'Cross-reference with known AI disinformation campaigns'],
  },
  {
    entity: 'pm_emergency_broadcast_audio.mp3', type: 'VoiceClone', suite: 'neural',
    reputation: 'Voice clone detected \u2014 ElevenLabs TTS signature, formant deviation 0.847 (high anomaly)',
    impact_context: 'ElevenLabs voice clone \u2013 PM impersonation audio, formant deviation 0.847, emergency broadcast framing',
    correlation_tags: ['Linked: Igor Petrov (operator)', 'Linked: minister_speech_clip.mp4 (same campaign)'],
    risk_breakdown: [{ label: 'Voice synthesis confidence', pct: 49, color: 'bg-pink-500' }, { label: 'Impersonation harm risk', pct: 30, color: 'bg-rose-500' }, { label: 'Distribution reach', pct: 14, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Government emergency broadcast hoax', exposure: 0.93, platforms: ['Telegram', 'WhatsApp'] }],
    velocity_projection: { impressions_48h: 4800000, viral_probability: 0.95, peak_hour: '2026-05-31T21:00:00' },
    risk_score: 0.93, source: 'Neural Flow (voice analysis)', timestamp: new Date().toISOString(), associated_post: 'telegram_4401',
    media_url: 'https://cdn.example.com/uploads/pm_emergency_broadcast_audio.mp3',
    ai_confidence: 0.93, generation_method: 'TTS / Voice Clone', model_attribution: 'ElevenLabs', frequency_anomaly: true, formant_deviation: 0.847,
    related_posts: ['telegram_4401', 'whatsapp_group_0019'],
    recommendations: ['Escalate as high-priority synthetic audio', 'Notify impersonated individual immediately', 'Request emergency platform takedown', 'Forward to national cybersecurity agency'],
  },
  {
    entity: 'press_conference_official_photo.jpg', type: 'NeuralWatermark', suite: 'neural',
    reputation: 'C2PA Content Credentials signature present but tampered \u2014 indicates re-editing after signing',
    impact_context: 'C2PA signature tampered \u2013 Official press photo re-edited after Adobe signing, provenance chain broken',
    correlation_tags: ['C2PA chain: BROKEN', 'Linked: activist_march_kyiv_2026.jpg (same editing pattern)'],
    risk_breakdown: [{ label: 'Watermark tampering confidence', pct: 42, color: 'bg-indigo-500' }, { label: 'Provenance breach risk', pct: 22, color: 'bg-rose-500' }, { label: 'Attribution manipulation', pct: 14, color: 'bg-orange-500' }],
    narrative_footprint: [{ narrative: 'Official image manipulation', exposure: 0.78, platforms: ['Facebook', 'Instagram'] }],
    velocity_projection: { impressions_48h: 1100000, viral_probability: 0.61, peak_hour: '2026-05-31T16:00:00' },
    risk_score: 0.78, source: 'C2PA / Neural Flow', timestamp: new Date().toISOString(), associated_post: 'facebook_6601',
    media_url: 'https://cdn.example.com/uploads/press_conference_official_photo.jpg',
    watermark_status: 'tampered', c2pa_issuer: 'Adobe Content Credentials (invalid signature)', ai_confidence: 0.78,
    related_posts: ['facebook_6601', 'instagram_7712'],
    recommendations: ['Flag as tampered official image', 'Request original from issuing photographer', 'Notify C2PA issuer of signature breach', 'Submit to digital provenance registry'],
  },
];

const SUITE_META: { key: OSINTFinding['suite']; label: string; icon: React.ReactNode; color: string; trend: number; correlation?: string; deepfake_note?: string }[] = [
  { key: 'cyber',      label: 'Cyber Threat',       icon: <Globe className="w-4 h-4" />,      color: 'text-blue-400',    trend: 12 },
  { key: 'identity',   label: 'Identity',            icon: <User className="w-4 h-4" />,       color: 'text-purple-400',  trend: 3 },
  { key: 'compliance', label: 'Compliance',          icon: <Building2 className="w-4 h-4" />,  color: 'text-amber-400',   trend: 7,  correlation: 'Linked via shell company + OFAC actor' },
  { key: 'media',      label: 'Media Forensics',     icon: <Film className="w-4 h-4" />,       color: 'text-fuchsia-400', trend: 18, deepfake_note: '2 probable deepfakes detected' },
  { key: 'geo',        label: 'Geo & Transport',     icon: <Navigation className="w-4 h-4" />, color: 'text-emerald-400', trend: 5,  correlation: 'Linked via EXIF + same VoIP carrier' },
  { key: 'neural',     label: 'Neural AI Forensics', icon: <Brain className="w-4 h-4" />,      color: 'text-violet-400',  trend: 22, deepfake_note: '2 probable deepfakes detected' },
];

const IntelligenceCenter: React.FC<IntelligenceCenterProps> = ({ monitoringScope }) => {
  const [findings, setFindings] = useState<OSINTFinding[]>(MOCK_FINDINGS);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFinding, setSelectedFinding] = useState<OSINTFinding | null>(null);
  const [modalTab, setModalTab]     = useState<'overview' | 'narrative' | 'velocity' | 'signals'>('overview');
  const [openAlerts, setOpenAlerts] = useState<number>(14);
  const [systemHealth, setSystemHealth] = useState<number>(82);
  const [viewMode, setViewMode]     = useState<'list' | 'graph'>('list');
  const [attackSurfaceOpen, setAttackSurfaceOpen] = useState(false);
  const [riskExpanded, setRiskExpanded] = useState(false);
  const [countdown, setCountdown]   = useState('2h 14m');
  const [lastUpdated, setLastUpdated] = useState('');
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepProgress, setSweepProgress] = useState(0);
  const [sweepPhase, setSweepPhase] = useState('');
  const [sweepDone, setSweepDone] = useState(false);
  const [archives, setArchives] = useState<ArchiveSummary[]>([]);
  // BI OSINT live intelligence integration
  const [biSignals, setBiSignals] = useState<{ id: number; title: string; severity: string; source_phase: string; ingested_at: string }[]>([]);
  const [biSignalsLoading, setBiSignalsLoading] = useState(false);
  const cycleSignals                = 184;
  const cycleDelta                  = 41;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('crisis_osint_archives');
      if (stored) setArchives(JSON.parse(stored));
    } catch {}
  }, []);

  // Fetch live BI OSINT signals for the Intelligence Center sidebar panel
  useEffect(() => {
    const loadFindings = async () => {
      setFindingsLoading(true);
      try {
        const live = await apiService.analytics.getIntelligenceFindings(60, monitoringScope);
        if (Array.isArray(live) && live.length > 0) {
          setFindings(live as OSINTFinding[]);
        } else {
          setFindings(MOCK_FINDINGS);
        }
      } catch {
        setFindings(MOCK_FINDINGS);
      } finally {
        setFindingsLoading(false);
      }
    };
    loadFindings();
  }, [monitoringScope]);

  useEffect(() => {
    const load = async () => {
      setBiSignalsLoading(true);
      try {
        const scopeQ = `${monitoringScope?.scope_type ? `&scope_type=${encodeURIComponent(monitoringScope.scope_type)}` : ''}` +
          `${monitoringScope?.country_code ? `&country_code=${encodeURIComponent(monitoringScope.country_code)}` : ''}` +
          `${monitoringScope?.region_code ? `&region_code=${encodeURIComponent(monitoringScope.region_code)}` : ''}`;
        // Fetch HIGH+ severity events from the BI intelligence fusion feed
        const res = await fetch(
          `${API_BASE_URL}/api/bi/fusion/feed/Technology?days=7&limit=8&severity_filter=HIGH${scopeQ}`
        );
        if (res.ok) {
          const data = await res.json();
          setBiSignals(Array.isArray(data) ? data : []);
        }
      } catch { /* non-blocking */ } finally {
        setBiSignalsLoading(false);
      }
    };
    load();
  }, [monitoringScope]);

  const handleNarrativeSweep = () => {
    if (sweepRunning) return;
    setSweepRunning(true);
    setSweepDone(false);
    setSweepProgress(0);
    setSweepPhase(SWEEP_PHASES[0]);
    let step = 0;
    const TOTAL = 75;
    const iv = setInterval(() => {
      step++;
      const pct = Math.min(Math.round((step / TOTAL) * 100), 100);
      setSweepProgress(pct);
      setSweepPhase(SWEEP_PHASES[Math.min(Math.floor(pct / 17), SWEEP_PHASES.length - 1)]);
      if (step >= TOTAL) {
        clearInterval(iv);
        setSweepPhase('Sweep complete — 17 signals classified · 3 new high-risk narratives confirmed');
        setSweepRunning(false);
        setSweepDone(true);
      }
    }, 80);
  };

  const handleArchiveSummary = () => {
    const now = new Date();
    const id = `arc_${now.getTime()}`;
    const filename = `osint_summary_${now.toISOString().replace(/[:.]/g, '-').slice(0, 19)}.json`;
    const highRisk = findings.filter(f => f.risk_score > 0.8).length;
    const payload = {
      id,
      generated_at: now.toISOString(),
      generated_by: 'CrisisShield Intelligence Center',
      summary: {
        total_findings: findings.length,
        high_risk_count: highRisk,
        suites_covered: [...new Set(findings.map(f => f.suite))],
        exported_by: 'client_export',
      },
      findings: findings.map(f => ({
        entity: f.entity,
        type: f.type,
        suite: f.suite,
        risk_score: f.risk_score,
        source: f.source,
        timestamp: f.timestamp,
        reputation: f.reputation,
        impact_context: f.impact_context,
        recommendations: f.recommendations,
      })),
    };
    const dataStr = JSON.stringify(payload, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    const entry: ArchiveSummary = {
      id,
      timestamp: now.toISOString(),
      findings_count: findings.length,
      high_risk_count: highRisk,
      title: `OSINT Sweep — ${now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      filename,
      data: dataStr,
    };
    const updated = [entry, ...archives].slice(0, 20);
    setArchives(updated);
    try { localStorage.setItem('crisis_osint_archives', JSON.stringify(updated)); } catch {}
  };

  useEffect(() => {
    apiService.metrics.getDashboardStats(monitoringScope).then(stats => {
      if (stats) {
        setOpenAlerts((stats as any).open_alerts ?? (stats as any).total_alerts ?? 14);
        setSystemHealth((stats as any).system_health ?? 82);
      }
    }).catch(() => {});
  }, [monitoringScope]);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setLastUpdated(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' IST');
      const totalSec = 8040 - Math.floor((Date.now() / 1000) % 8040);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      setCountdown(`${h}h ${m}m`);
    };
    update();
    timerRef.current = setInterval(update, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const filteredFindings = findings.filter(f =>
    f.entity.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.reputation.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.impact_context ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const suiteCounts = SUITE_META.map(s => ({
    ...s,
    count: findings.filter(f => f.suite === s.key).length,
    urgentCount: findings.filter(f => f.suite === s.key && f.risk_score > 0.8).length,
  }));

  const Chip: React.FC<{ label: string; icon: React.ReactNode; color?: string }> = ({ label, icon, color = 'bg-slate-800 text-slate-300 border-slate-700 hover:border-cyan-500 hover:text-cyan-300' }) => (
    <button className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border font-semibold transition-all ${color}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="p-8 bg-slate-950 min-h-screen text-slate-200">
      <div className="w-full">

        {/* Header */}
        <header className="mb-6 flex justify-between items-start gap-6">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <Shield className="mr-3 text-cyan-400 w-10 h-10" />
              Intelligence Center
            </h1>
            <p className="text-slate-400">Enterprise OSINT Forensics · Crisis Narrative Command</p>
            {findingsLoading && (
              <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" /> Loading live findings...</p>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 flex items-center w-96 shadow-2xl focus-within:border-cyan-500 transition-all">
              <Search className="text-slate-500 mr-3 w-5 h-5 shrink-0" />
              <input
                type="text"
                placeholder="Search by entity, narrative keyword, or risk vector (e.g., election-truth.news)"
                className="bg-transparent border-none outline-none text-white w-full text-sm placeholder:text-slate-600"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="text-slate-500 hover:text-white ml-2 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-600 flex items-center gap-1.5 pr-1">
              <Clock className="w-3 h-3" /> Last Updated: <span className="text-slate-400 font-mono">{lastUpdated}</span>
              <button className="ml-1 text-cyan-500 hover:text-cyan-300"><RefreshCw className="w-3 h-3" /></button>
            </p>
          </div>
        </header>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Total Findings', value: findings.length,
              icon: <Database className="w-5 h-5" />, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20',
            },
            {
              label: 'Critical Risk', value: findings.filter(f => f.risk_score > 0.8).length,
              icon: <ShieldAlert className="w-5 h-5" />, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20',
              pulse: findings.filter(f => f.risk_score > 0.8).length > 0,
            },
            {
              label: 'Active Suites', value: new Set(findings.map(f => f.suite)).size,
              icon: <Layers className="w-5 h-5" />, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20',
            },
            {
              label: 'Avg Risk Score', value: `${findings.length ? Math.round((findings.reduce((s, f) => s + f.risk_score, 0) / findings.length) * 100) : 0}%`,
              icon: <TrendingUp className="w-5 h-5" />, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20',
            },
          ].map(k => (
            <div key={k.label} className={`rounded-xl border ${k.bg} p-4 flex items-center gap-3`}>
              <div className={`${k.color} relative`}>
                {k.icon}
                {(k as { pulse?: boolean }).pulse && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-rose-400 rounded-full animate-ping" />}
              </div>
              <div>
                <p className={`text-2xl font-black ${k.color}`}>{k.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{k.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Crisis Pulse Banner */}
        <div className="mb-8 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-rose-500/10 border border-amber-500/30 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-amber-500/15 rounded-xl border border-amber-500/30 shrink-0">
                <Radio className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-1.5">
                  <p className="text-sm font-black text-amber-300 uppercase tracking-wider">7-Day Misinfo Cycle Complete</p>
                  <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">
                    <TrendingUp className="w-3 h-3" /> +{cycleDelta}% from last cycle
                  </span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20">
                    {cycleSignals} new signals detected
                  </span>
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  Spike driven by <span className="text-amber-300 font-semibold">coordinated domain registrations</span> +{' '}
                  <span className="text-rose-300 font-semibold">3 high-risk signals pending review</span>
                </p>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Clock className="w-3 h-3 text-cyan-500" />
                  <span>Next sync in</span>
                  <span className="font-mono font-bold text-cyan-400">{countdown}</span>
                  <span className="mx-1">·</span>
                  <span className="text-rose-400 font-semibold">3 high-risk signals pending</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap shrink-0">
              <button
                onClick={handleArchiveSummary}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-cyan-900/30"
              >
                <Download className="w-4 h-4" /> Archive & Generate Client Summary
              </button>
              <button
                onClick={handleNarrativeSweep}
                disabled={sweepRunning}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-bold transition-all ${
                  sweepRunning
                    ? 'bg-rose-500/5 border-rose-500/20 text-rose-500/50 cursor-not-allowed'
                    : 'bg-rose-500/15 hover:bg-rose-500/25 border-rose-500/40 text-rose-300'
                }`}
              >
                <Zap className="w-4 h-4" /> {sweepRunning ? 'Sweeping...' : 'Initiate Full Narrative Sweep'}
              </button>
            </div>
          </div>
          {sweepRunning && (
            <div className="mt-3 bg-slate-900/70 border border-cyan-500/20 rounded-xl px-5 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Activity className="w-3 h-3 animate-pulse" /> Narrative Sweep In Progress</p>
                <span className="text-xs font-mono text-cyan-300">{sweepProgress}%</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-500 rounded-full transition-all duration-100" style={{ width: `${sweepProgress}%` }} />
              </div>
              <p className="text-xs text-slate-400">{sweepPhase}</p>
            </div>
          )}
          {sweepDone && !sweepRunning && (
            <div className="mt-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-5 py-3 flex items-center gap-3">
              <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
              <p className="text-xs text-emerald-300">{sweepPhase}</p>
              <button className="ml-auto text-xs text-slate-500 hover:text-slate-300 transition-colors" onClick={() => setSweepDone(false)}>dismiss</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">

            {/* Resilience Cockpit */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-6 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Misinfo Resilience Cockpit</h3>
                <span className="text-xs text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" />{lastUpdated}</span>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-slate-300 text-sm">Active Probes</span>
                    <p className="text-xs text-slate-600 mt-0.5">across 9 misinfo vectors</p>
                  </div>
                  <span className="text-cyan-400 font-mono font-black text-lg">{openAlerts}</span>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-slate-300 text-sm">Daily API Usage</span>
                    <span className={`font-mono text-sm font-bold ${systemHealth > 80 ? 'text-emerald-400' : systemHealth > 60 ? 'text-amber-400' : 'text-rose-400'}`}>{systemHealth}%</span>
                  </div>
                  <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${systemHealth > 80 ? 'bg-emerald-500' : systemHealth > 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${systemHealth}%` }} />
                  </div>
                  {systemHealth > 80 && <p className="text-xs text-emerald-600 mt-1">✓ Healthy — below warning threshold</p>}
                  {systemHealth <= 80 && systemHealth > 60 && <p className="text-xs text-amber-500 mt-1">⚠ Approaching throttle limit</p>}
                </div>
                <div className="bg-slate-800/40 rounded-xl p-3 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400 flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-cyan-400" /> Misinfo Surface Coverage
                      <span title="% of known misinfo vectors actively monitored"><Info className="w-3 h-3 text-slate-600" /></span>
                    </span>
                    <span className="text-cyan-400 font-black font-mono">94%</span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: '94%' }} />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">6% vectors unmonitored (dark web)</p>
                </div>
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3">
                  <p className="text-xs text-emerald-400 font-bold mb-1 flex items-center gap-1.5"><Activity className="w-3 h-3" /> AI Health Insight</p>
                  <p className="text-xs text-slate-400 leading-relaxed">All probes stable. No throttling detected from known disinformation infrastructure. <span className="text-emerald-300 font-semibold">Neural pipeline 98ms avg latency.</span></p>
                </div>
              </div>
            </div>

            {/* Signal Prioritizer */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-6 rounded-2xl shadow-xl">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Intelligent Signal Prioritizer</h3>
              <button onClick={() => setAttackSurfaceOpen(v => !v)} className="w-full mb-4 text-left bg-rose-500/10 hover:bg-rose-500/15 border border-rose-500/30 rounded-xl p-3 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-rose-300 flex items-center gap-1.5"><BarChart2 className="w-3.5 h-3.5" /> Misinfo Attack Surface</p>
                    <p className="text-xl font-black text-rose-400 mt-0.5">87<span className="text-xs font-normal text-slate-500">/100</span></p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-rose-300 font-bold">Elevated</span>
                    <p className="text-xs text-slate-600 mt-0.5">narrative hijacking risk</p>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-500 mx-auto mt-1 transition-transform ${attackSurfaceOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {attackSurfaceOpen && (
                  <div className="mt-3 pt-3 border-t border-rose-500/20 text-xs text-slate-400 space-y-1 leading-relaxed">
                    <p>· <span className="text-rose-300 font-semibold">47 pts</span> — Active coordinated campaigns detected</p>
                    <p>· <span className="text-amber-300 font-semibold">28 pts</span> — AI-generated content volume spike</p>
                    <p>· <span className="text-orange-300 font-semibold">12 pts</span> — Sanctioned actor infrastructure active</p>
                  </div>
                )}
              </button>
              <div className="space-y-2.5">
                {suiteCounts.map(s => (
                  <div key={s.key} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 ${s.urgentCount > 1 ? 'border-rose-500 bg-rose-500/10' : s.urgentCount === 1 ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 bg-slate-800/40'}`}>
                      <span className={s.color}>{s.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 text-xs font-medium">{s.label}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`${s.color} font-mono font-black text-sm`}>{s.count}</span>
                          <span className={`text-xs flex items-center gap-0.5 ${s.trend > 10 ? 'text-rose-400' : s.trend > 5 ? 'text-amber-400' : 'text-slate-500'}`}>
                            <TrendingUp className="w-2.5 h-2.5" />+{s.trend}%
                          </span>
                        </div>
                      </div>
                      {(s.correlation || s.deepfake_note) && (
                        <p className="text-xs text-slate-600 mt-0.5 leading-tight truncate">
                          <span className="text-cyan-600">↗</span> {s.deepfake_note ?? s.correlation}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

            {/* BI OSINT Intelligence Panel — live signals from Phase 1-5 */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-violet-500/20 p-4 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                  <Briefcase className="w-3 h-3 text-violet-400" />
                  <span className="text-violet-400">BI OSINT Signals</span>
                </h3>
                {biSignalsLoading ? (
                  <RefreshCw className="w-3 h-3 text-violet-400 animate-spin" />
                ) : (
                  <span className="text-xs text-slate-600">{biSignals.length} live</span>
                )}
              </div>
              {biSignalsLoading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <div key={i} className="animate-pulse bg-slate-800 h-10 rounded-lg" />)}
                </div>
              ) : biSignals.length === 0 ? (
                <p className="text-xs text-slate-600 text-center py-3 leading-relaxed">
                  No high-severity BI signals.<br/>
                  <span className="text-violet-400/60">Configure in Business Intelligence → Intelligence Feed</span>
                </p>
              ) : (
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-0.5">
                  {biSignals.map(s => {
                    const sColor = s.severity === 'CRITICAL' ? 'text-rose-400 border-rose-500/20' : s.severity === 'HIGH' ? 'text-amber-400 border-amber-500/20' : 'text-cyan-400 border-cyan-500/20';
                    return (
                      <div key={s.id} className={`flex items-start gap-2 bg-slate-800/50 border rounded-lg px-2.5 py-2 ${sColor}`}>
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-bold uppercase leading-none">{s.severity}</span>
                            <span className="text-xs text-slate-600">{s.source_phase}</span>
                          </div>
                          <p className="text-xs text-slate-300 truncate mt-0.5 leading-tight">{s.title}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-slate-700 mt-2 flex items-center gap-1">
                <Activity className="w-2.5 h-2.5" /> Technology sector · last 7 days
              </p>
            </div>

            {/* Archived Summaries */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-4 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Archive className="w-3 h-3" /> Archived Summaries</h3>
                <span className="text-xs text-slate-600">{archives.length} saved</span>
              </div>
              {archives.length === 0 ? (
                <p className="text-xs text-slate-600 text-center py-3 leading-relaxed">No archives yet.<br/>Click &ldquo;Archive &amp; Generate Client Summary&rdquo; to save your first report.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-0.5">
                  {archives.slice(0, 15).map(a => (
                    <div key={a.id} className="bg-slate-800/50 rounded-lg px-3 py-2.5 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-300 truncate">{a.title}</p>
                        <p className="text-xs text-slate-500">{a.findings_count} signals &middot; {a.high_risk_count} high-risk</p>
                        <p className="text-xs text-slate-700 font-mono">{new Date(a.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <button
                        title={`Re-download ${a.filename}`}
                        onClick={() => {
                          const blob = new Blob([a.data], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const el = document.createElement('a');
                          el.href = url; el.download = a.filename; el.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="shrink-0 p-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-500 hover:text-cyan-300 transition-colors mt-0.5"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          {/* Discovery Feed */}
          <div className="lg:col-span-3 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Latest OSINT Signal Discovery</h2>
                <p className="text-xs text-slate-500 mt-0.5">{filteredFindings.length} signals · sorted by risk score</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/40' : 'text-slate-500 border-slate-800 hover:border-slate-700'}`}>
                  <Layers className="w-3.5 h-3.5" /> List View
                </button>
                <button onClick={() => setViewMode('graph')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-1.5 ${viewMode === 'graph' ? 'bg-violet-500/10 text-violet-300 border-violet-500/40' : 'text-slate-500 border-slate-800 hover:border-slate-700'}`}>
                  <Network className="w-3.5 h-3.5" /> Graph View
                </button>
              </div>
            </div>

            {viewMode === 'graph' && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
                {/* Graph Header */}
                <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-sm font-bold text-white flex items-center gap-2">
                      <Network className="w-4 h-4 text-violet-400" /> Narrative Connection Map
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">9 entities · 1 coordinated influence operation · real-time link analysis</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-full bg-rose-500 opacity-80" /> Critical (&gt;80%)
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-full bg-amber-500 opacity-80" /> High (60–80%)
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-4" style={{ borderTop: '2px solid rgba(244,63,94,0.55)', height: '1px' }} /> Confirmed link
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-4" style={{ borderTop: '1px dashed rgba(34,211,238,0.45)', height: '1px' }} /> Infra link
                    </span>
                  </div>
                </div>

                {/* SVG Graph Canvas */}
                <div className="bg-slate-950/60">
                  <svg viewBox="0 0 580 375" className="w-full" style={{ height: '375px' }}>
                    <defs>
                      {/* Arrowheads */}
                      <marker id="ah-r" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                        <path d="M0,0.5 L6,3 L0,5.5 Z" fill="#f43f5e" fillOpacity="0.75" />
                      </marker>
                      <marker id="ah-a" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                        <path d="M0,0.5 L6,3 L0,5.5 Z" fill="#f59e0b" fillOpacity="0.75" />
                      </marker>
                      <marker id="ah-c" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                        <path d="M0,0.5 L6,3 L0,5.5 Z" fill="#22d3ee" fillOpacity="0.6" />
                      </marker>
                      {/* Glow filter for high-risk nodes */}
                      <filter id="glow-n" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="3.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                      </filter>
                      {/* Dot grid background */}
                      <pattern id="dotgrid" width="18" height="18" patternUnits="userSpaceOnUse">
                        <circle cx="9" cy="9" r="0.65" fill="rgba(148,163,184,0.07)" />
                      </pattern>
                    </defs>

                    {/* Background */}
                    <rect width="580" height="375" fill="url(#dotgrid)" />

                    {/* ── EDGES ── */}

                    {/* TruthMedia Holdings → Igor Petrov */}
                    <line x1="146" y1="94" x2="302" y2="159" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" markerEnd="url(#ah-a)" />
                    <text x="218" y="117" fill="#f59e0b" fontSize="7.5" textAnchor="middle" opacity="0.75">board member</text>

                    {/* TruthMedia Holdings → factcheck-news.tk */}
                    <line x1="120" y1="101" x2="87" y2="184" stroke="#f43f5e" strokeWidth="1.5" strokeOpacity="0.45" markerEnd="url(#ah-r)" />
                    <text x="88" y="142" fill="#f43f5e" fontSize="7" textAnchor="middle" opacity="0.65">owns domain</text>

                    {/* factcheck-news.tk → 192.168.4.152 */}
                    <line x1="70" y1="212" x2="55" y2="279" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.4" markerEnd="url(#ah-r)" />
                    <text x="43" y="246" fill="#f43f5e" fontSize="6.5" textAnchor="middle" opacity="0.6">hosts</text>

                    {/* factcheck-news.tk ⟶ election-truth.news (registrar / dashed) */}
                    <line x1="91" y1="193" x2="486" y2="89" stroke="#22d3ee" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5,4" markerEnd="url(#ah-c)" />
                    <text x="292" y="130" fill="#22d3ee" fontSize="7" textAnchor="middle" opacity="0.5">registrar linked</text>

                    {/* Igor Petrov → minister_speech_clip.mp4 */}
                    <line x1="338" y1="179" x2="494" y2="232" stroke="#f43f5e" strokeWidth="1.5" strokeOpacity="0.55" markerEnd="url(#ah-r)" />
                    <text x="424" y="198" fill="#f43f5e" fontSize="7.5" textAnchor="middle" opacity="0.75">operator</text>

                    {/* Igor Petrov → pm_emergency_broadcast_audio */}
                    <line x1="328" y1="191" x2="378" y2="296" stroke="#f43f5e" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#ah-r)" />
                    <text x="371" y="249" fill="#f43f5e" fontSize="7" textAnchor="middle" opacity="0.7">same campaign</text>

                    {/* pm_emergency_audio → minister_speech_clip (same op) */}
                    <line x1="404" y1="304" x2="498" y2="254" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.38" markerEnd="url(#ah-r)" />
                    <text x="462" y="288" fill="#f43f5e" fontSize="6.5" textAnchor="middle" opacity="0.58">same op</text>

                    {/* activist_march_kyiv.jpg → Igor Petrov */}
                    <line x1="220" y1="295" x2="304" y2="192" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.38" markerEnd="url(#ah-r)" />
                    <text x="252" y="249" fill="#f43f5e" fontSize="6.5" textAnchor="middle" opacity="0.58">campaign asset</text>

                    {/* +1 (555) VoIP → Igor Petrov */}
                    <line x1="178" y1="175" x2="302" y2="175" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.35" markerEnd="url(#ah-a)" />
                    <text x="240" y="168" fill="#f59e0b" fontSize="6.5" textAnchor="middle" opacity="0.55">VoIP linked</text>

                    {/* ── NODES ── */}

                    {/* Igor Petrov — central hub (largest, glow) */}
                    <circle cx="320" cy="175" r="27" fill="#f43f5e" fillOpacity="0.07" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.2" filter="url(#glow-n)" />
                    <circle cx="320" cy="175" r="20" fill="#150b1a" stroke="#f43f5e" strokeWidth="2" />
                    <text x="320" y="171" fill="#f43f5e" fontSize="7.5" fontWeight="bold" textAnchor="middle">OFAC SDN</text>
                    <text x="320" y="182" fill="#f43f5e" fontSize="8" fontWeight="bold" textAnchor="middle">97%</text>
                    <text x="320" y="209" fill="#fca5a5" fontSize="10" fontWeight="bold" textAnchor="middle">Igor Petrov</text>
                    <text x="320" y="221" fill="#7f1d1d" fontSize="7" textAnchor="middle">Sanctioned Actor</text>

                    {/* TruthMedia Holdings */}
                    <circle cx="130" cy="86" r="16" fill="#f59e0b" fillOpacity="0.1" stroke="#f59e0b" strokeWidth="1.5" />
                    <text x="130" y="90" fill="#f59e0b" fontSize="8.5" fontWeight="bold" textAnchor="middle">78%</text>
                    <text x="130" y="111" fill="#fde68a" fontSize="8.5" fontWeight="bold" textAnchor="middle">TruthMedia</text>
                    <text x="130" y="122" fill="#78350f" fontSize="7" textAnchor="middle">Holdings Ltd.</text>

                    {/* factcheck-news.tk */}
                    <circle cx="75" cy="198" r="14" fill="#f43f5e" fillOpacity="0.1" stroke="#f43f5e" strokeWidth="1.5" />
                    <text x="75" y="202" fill="#f43f5e" fontSize="8" fontWeight="bold" textAnchor="middle">92%</text>
                    <text x="75" y="223" fill="#fca5a5" fontSize="7.5" fontWeight="bold" textAnchor="middle">factcheck-news</text>
                    <text x="75" y="233" fill="#7f1d1d" fontSize="6.5" textAnchor="middle">.tk domain</text>

                    {/* 192.168.4.152 — C2 node */}
                    <circle cx="52" cy="293" r="12" fill="#f43f5e" fillOpacity="0.08" stroke="#f43f5e" strokeWidth="1" />
                    <text x="52" y="297" fill="#f43f5e" fontSize="7.5" fontWeight="bold" textAnchor="middle">85%</text>
                    <text x="52" y="316" fill="#fca5a5" fontSize="7.5" fontWeight="bold" textAnchor="middle">192.168.4.152</text>
                    <text x="52" y="326" fill="#7f1d1d" fontSize="6.5" textAnchor="middle">C2 / SSH BruteF</text>

                    {/* election-truth.news */}
                    <circle cx="500" cy="86" r="12" fill="#f59e0b" fillOpacity="0.08" stroke="#f59e0b" strokeWidth="1" />
                    <text x="500" y="90" fill="#f59e0b" fontSize="7.5" fontWeight="bold" textAnchor="middle">71%</text>
                    <text x="500" y="109" fill="#fde68a" fontSize="7.5" fontWeight="bold" textAnchor="middle">election-truth</text>
                    <text x="500" y="119" fill="#78350f" fontSize="6.5" textAnchor="middle">.news [deleted]</text>

                    {/* minister_speech_clip.mp4 — deepfake */}
                    <circle cx="511" cy="244" r="15" fill="#f43f5e" fillOpacity="0.1" stroke="#f43f5e" strokeWidth="1.5" filter="url(#glow-n)" />
                    <text x="511" y="248" fill="#f43f5e" fontSize="7.5" fontWeight="bold" textAnchor="middle">94%</text>
                    <text x="511" y="268" fill="#fca5a5" fontSize="7.5" fontWeight="bold" textAnchor="middle">minister_speech</text>
                    <text x="511" y="278" fill="#7f1d1d" fontSize="6.5" textAnchor="middle">[deepfake mp4]</text>

                    {/* pm_emergency_broadcast_audio — voice clone */}
                    <circle cx="390" cy="310" r="14" fill="#f43f5e" fillOpacity="0.1" stroke="#f43f5e" strokeWidth="1.5" filter="url(#glow-n)" />
                    <text x="390" y="314" fill="#f43f5e" fontSize="7.5" fontWeight="bold" textAnchor="middle">93%</text>
                    <text x="390" y="334" fill="#fca5a5" fontSize="7.5" fontWeight="bold" textAnchor="middle">pm_emergency</text>
                    <text x="390" y="344" fill="#7f1d1d" fontSize="6.5" textAnchor="middle">[voice clone]</text>

                    {/* activist_march_kyiv_2026.jpg — AI image */}
                    <circle cx="210" cy="306" r="13" fill="#f43f5e" fillOpacity="0.1" stroke="#f43f5e" strokeWidth="1.5" />
                    <text x="210" y="310" fill="#f43f5e" fontSize="7.5" fontWeight="bold" textAnchor="middle">91%</text>
                    <text x="210" y="330" fill="#fca5a5" fontSize="7.5" fontWeight="bold" textAnchor="middle">activist_march</text>
                    <text x="210" y="340" fill="#7f1d1d" fontSize="6.5" textAnchor="middle">[AI image SDXL]</text>

                    {/* +1 (555) 019-2831 — VoIP */}
                    <circle cx="165" cy="175" r="12" fill="#f59e0b" fillOpacity="0.08" stroke="#f59e0b" strokeWidth="1" />
                    <text x="165" y="179" fill="#f59e0b" fontSize="7.5" fontWeight="bold" textAnchor="middle">75%</text>
                    <text x="165" y="198" fill="#fde68a" fontSize="7.5" fontWeight="bold" textAnchor="middle">+1(555) VoIP</text>
                    <text x="165" y="208" fill="#78350f" fontSize="6.5" textAnchor="middle">Twilio high-risk</text>

                    {/* ── ANIMATED PARTICLES along key edges ── */}
                    <circle r="2.5" fill="#f43f5e" opacity="0.85">
                      <animateMotion dur="2s" repeatCount="indefinite" path="M146,94 L302,159" />
                    </circle>
                    <circle r="2" fill="#f43f5e" opacity="0.75">
                      <animateMotion dur="2.4s" repeatCount="indefinite" path="M328,191 L378,296" />
                    </circle>
                    <circle r="2" fill="#f43f5e" opacity="0.75">
                      <animateMotion dur="1.9s" repeatCount="indefinite" path="M338,179 L494,232" />
                    </circle>
                    <circle r="1.8" fill="#f59e0b" opacity="0.7">
                      <animateMotion dur="2.8s" repeatCount="indefinite" path="M120,101 L87,184" />
                    </circle>
                  </svg>
                </div>

                {/* Graph Footer */}
                <div className="px-6 py-3 border-t border-slate-800 bg-slate-950/40 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3" style={{ borderTop: '2px solid rgba(244,63,94,0.55)', height: '1px' }} /> Confirmed entity link
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3" style={{ borderTop: '1px dashed rgba(34,211,238,0.45)', height: '1px' }} /> Infrastructure / registrar link
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500/50 mr-0.5" /> Animated particles = active data flow
                  </span>
                  <span className="ml-auto text-slate-500">Click entities in list view to investigate</span>
                </div>
              </div>
            )}

            {viewMode === 'list' && [...filteredFindings].sort((a, b) => b.risk_score - a.risk_score).map((finding, idx) => (
              <div key={idx} className={`group relative bg-slate-900 border rounded-2xl p-5 transition-all shadow-xl ${finding.risk_score > 0.8 ? 'border-rose-500/20 hover:border-rose-500/50 hover:shadow-rose-900/30 hover:shadow-2xl' : finding.risk_score > 0.6 ? 'border-amber-500/20 hover:border-amber-500/40 hover:shadow-amber-900/20 hover:shadow-xl' : 'border-slate-800 hover:border-slate-600'}`}>
                {/* Hover Preview Tooltip */}
                <div className="absolute bottom-full left-0 mb-2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 w-80">
                  <div className="bg-slate-800/95 backdrop-blur-md border border-slate-600/70 rounded-xl p-3.5 shadow-2xl shadow-black/80">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Quick Context</span>
                      <span className={`text-lg font-black font-mono ${riskColor(finding.risk_score)}`}>{(finding.risk_score * 100).toFixed(0)}%</span>
                    </div>
                    {finding.impact_context && (
                      <p className="text-xs text-slate-300 leading-relaxed mb-2">{finding.impact_context}</p>
                    )}
                    <div className="space-y-1.5">
                      {finding.velocity_projection && (
                        <div className="flex items-center gap-1.5 text-xs text-violet-400 font-semibold">
                          <TrendingUp className="w-2.5 h-2.5 shrink-0" />
                          <span>{finding.velocity_projection.impressions_48h >= 1e6 ? `${(finding.velocity_projection.impressions_48h / 1e6).toFixed(1)}M` : `${(finding.velocity_projection.impressions_48h / 1e3).toFixed(0)}K`} projected · {(finding.velocity_projection.viral_probability * 100).toFixed(0)}% viral prob</span>
                        </div>
                      )}
                      {finding.correlation_tags?.[0] && (
                        <div className="flex items-center gap-1.5 text-xs text-cyan-400 font-mono">
                          <GitBranch className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{finding.correlation_tags[0]}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="absolute -bottom-1.5 left-5 w-3 h-3 bg-slate-800/95 border-b border-r border-slate-600/70 rotate-45" />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className={`relative p-3 rounded-xl shrink-0 ${typeStyle(finding.type)}`}>
                      {typeIcon(finding.type)}
                      {finding.risk_score > 0.8 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full border-2 border-slate-900 animate-pulse" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h4 className="text-base font-mono font-bold text-white truncate max-w-xs">{finding.entity}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded border font-semibold uppercase tracking-tighter ${typeStyle(finding.type)} border-current/20`}>{finding.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded border font-medium ${suiteBadge(finding.suite)}`}>Suite {suiteNum(finding.suite)}</span>
                      </div>
                      <p className="text-slate-400 text-sm mb-2 line-clamp-1">{finding.reputation}</p>
                      {finding.impact_context && (
                        <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border mb-3 text-xs ${riskBg(finding.risk_score)}`}>
                          <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${riskColor(finding.risk_score)}`} />
                          <span className={`font-semibold leading-relaxed ${riskColor(finding.risk_score)}`}>{finding.impact_context}</span>
                        </div>
                      )}
                      {finding.correlation_tags && finding.correlation_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {finding.correlation_tags.map((tag, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-lg bg-slate-800 border border-slate-700 text-cyan-400 font-mono flex items-center gap-1">
                              <GitBranch className="w-2.5 h-2.5" /> {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-1"><LinkIcon className="w-3 h-3" />{finding.source}</span>
                        <span className={`flex items-center gap-1 font-bold ${riskColor(finding.risk_score)}`}><ShieldAlert className="w-3 h-3" /> {(finding.risk_score * 100).toFixed(0)}% risk</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(finding.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        {finding.velocity_projection && (
                          <span className="flex items-center gap-1 text-violet-400 font-semibold">
                            <TrendingUp className="w-3 h-3" /> {(finding.velocity_projection.impressions_48h / 1e6).toFixed(1)}M projected impressions
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Chip label="Link to Narrative Graph" icon={<Network className="w-3 h-3" />} />
                        <Chip label="Generate Takedown Package" icon={<FileText className="w-3 h-3" />} color="bg-rose-500/10 text-rose-300 border-rose-500/30 hover:border-rose-400 hover:text-rose-200" />
                        <Chip label="Add to Crisis Brief" icon={<CheckCircle className="w-3 h-3" />} color="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 hover:border-cyan-400 hover:text-cyan-200" />
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedFinding(finding); setModalTab('overview'); setRiskExpanded(false); }} className="px-3 py-2 bg-slate-800 text-white rounded-xl text-xs hover:bg-cyan-600 transition-colors flex items-center gap-1.5 shrink-0 font-bold border border-slate-700 hover:border-cyan-500">
                    <ExternalLink className="w-3.5 h-3.5" /> Investigate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedFinding && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedFinding(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="flex items-start justify-between px-8 pt-7 pb-5 border-b border-slate-800 shrink-0">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Executive Crisis Report · OSINT Deep-Dive</p>
                <h2 className="text-xl font-bold text-white font-mono truncate max-w-lg">{selectedFinding.entity}</h2>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border rounded font-semibold uppercase ${typeStyle(selectedFinding.type)} border-current/20`}>{typeIcon(selectedFinding.type)} {selectedFinding.type}</span>
                  <span className={`inline-flex items-center text-xs px-2 py-1 border rounded font-medium ${suiteBadge(selectedFinding.suite)}`}>Suite {suiteLabel(selectedFinding.suite)}</span>
                </div>
              </div>
              <button onClick={() => setSelectedFinding(null)} className="p-2 hover:bg-slate-800 rounded-xl text-slate-500 hover:text-white transition-all"><X className="w-5 h-5" /></button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-8 py-3 border-b border-slate-800 bg-slate-950/50 shrink-0">
              {([
                { key: 'overview',  label: 'Overview',           icon: <Eye className="w-3.5 h-3.5" /> },
                { key: 'narrative', label: 'Narrative Footprint', icon: <Network className="w-3.5 h-3.5" /> },
                { key: 'velocity',  label: 'Velocity Projection', icon: <TrendingUp className="w-3.5 h-3.5" /> },
                { key: 'signals',   label: 'Associated Signals',  icon: <Layers className="w-3.5 h-3.5" /> },
              ] as const).map(tab => (
                <button key={tab.key} onClick={() => setModalTab(tab.key)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${modalTab === tab.key ? 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/40' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}`}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="overflow-y-auto flex-1 px-8 py-6">

              {modalTab === 'overview' && (
                <div className="space-y-5">
                  {/* Risk score + expandable breakdown */}
                  <div className={`p-4 rounded-xl border ${riskBg(selectedFinding.risk_score)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-2 font-bold text-sm">
                        <ShieldAlert className={`w-4 h-4 ${riskColor(selectedFinding.risk_score)}`} />
                        <span className={riskColor(selectedFinding.risk_score)}>Threat Risk Score</span>
                      </span>
                      <div className="flex items-center gap-3">
                        <span className={`text-2xl font-black font-mono ${riskColor(selectedFinding.risk_score)}`}>{(selectedFinding.risk_score * 100).toFixed(0)}%</span>
                        <button onClick={() => setRiskExpanded(v => !v)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 border border-slate-700 rounded-lg px-2 py-1 hover:bg-slate-800 transition-all">
                          Breakdown <ChevronDown className={`w-3 h-3 transition-transform ${riskExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${riskBar(selectedFinding.risk_score)}`} style={{ width: `${selectedFinding.risk_score * 100}%` }} />
                    </div>
                    {riskExpanded && selectedFinding.risk_breakdown && (
                      <div className="space-y-2 mt-4">
                        {selectedFinding.risk_breakdown.map((rb, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{rb.label}</span><span className="text-white font-bold font-mono">{rb.pct}%</span></div>
                            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full rounded-full ${rb.color}`} style={{ width: `${rb.pct}%` }} /></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedFinding.impact_context && (
                    <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${riskBg(selectedFinding.risk_score)}`}>
                      <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${riskColor(selectedFinding.risk_score)}`} />
                      <p className={`font-semibold ${riskColor(selectedFinding.risk_score)}`}>{selectedFinding.impact_context}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Database className="w-3 h-3" /> Reputation</p>
                      <p className="text-sm text-slate-200">{selectedFinding.reputation}</p>
                    </div>
                    {selectedFinding.country && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Country</p>
                        <p className="text-sm text-slate-200">{selectedFinding.country}</p>
                      </div>
                    )}
                    {selectedFinding.abuse_reports !== undefined && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Abuse Reports</p>
                        <p className="text-sm text-rose-400 font-bold font-mono">{selectedFinding.abuse_reports} confirmed</p>
                      </div>
                    )}
                    {selectedFinding.isp && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">ISP</p>
                        <p className="text-sm text-slate-200">{selectedFinding.isp}</p>
                      </div>
                    )}
                    {selectedFinding.asn && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">ASN</p>
                        <p className="text-sm text-slate-200 font-mono">{selectedFinding.asn}</p>
                      </div>
                    )}
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><LinkIcon className="w-3 h-3" /> Data Source</p>
                      <p className="text-sm text-cyan-400 font-medium">{selectedFinding.source}</p>
                    </div>
                    {selectedFinding.phone_carrier && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Phone Carrier / Line Type</p>
                        <p className="text-sm text-slate-200">{selectedFinding.phone_carrier}</p>
                        {selectedFinding.line_type && <p className="text-xs text-slate-400 mt-0.5 uppercase">{selectedFinding.line_type}</p>}
                      </div>
                    )}
                    {selectedFinding.company_number && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Company No.</p>
                        <p className="text-sm text-amber-400 font-mono">{selectedFinding.company_number}</p>
                      </div>
                    )}
                    {selectedFinding.jurisdiction && (
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Jurisdiction</p>
                        <p className="text-sm text-slate-200">{selectedFinding.jurisdiction}</p>
                      </div>
                    )}
                    {selectedFinding.sanctions_list && (
                      <div className="col-span-2 bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
                        <p className="text-xs text-rose-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Flag className="w-3 h-3" /> Sanctions Lists</p>
                        <p className="text-sm text-rose-300 font-medium">{selectedFinding.sanctions_list}</p>
                      </div>
                    )}
                    {selectedFinding.deepfake_score !== undefined && (
                      <div className="col-span-2 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs text-fuchsia-400 uppercase tracking-widest flex items-center gap-1.5"><Film className="w-3 h-3" /> Deepfake Probability</p>
                          <span className="text-fuchsia-300 font-black font-mono text-xl">{(selectedFinding.deepfake_score * 100).toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-fuchsia-500 rounded-full" style={{ width: `${selectedFinding.deepfake_score * 100}%` }} /></div>
                      </div>
                    )}
                    {selectedFinding.sha256_hash && (
                      <div className="col-span-2 bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Hash className="w-3 h-3" /> SHA-256 Hash</p>
                        <p className="text-xs text-lime-400 font-mono break-all">{selectedFinding.sha256_hash}</p>
                      </div>
                    )}
                    {selectedFinding.archive_url && (
                      <div className="col-span-2 bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                        <p className="text-xs text-cyan-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Archive className="w-3 h-3" /> Wayback Archive</p>
                        <a href={selectedFinding.archive_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-300 font-mono break-all hover:underline">{selectedFinding.archive_url}</a>
                        {selectedFinding.archive_timestamp && <p className="text-xs text-slate-500 mt-1">Captured: {new Date(selectedFinding.archive_timestamp).toLocaleString()}</p>}
                      </div>
                    )}
                    {selectedFinding.threat_categories && selectedFinding.threat_categories.length > 0 && (
                      <div className="col-span-2 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                        <p className="text-xs text-blue-300 uppercase tracking-widest mb-2">Threat Categories</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedFinding.threat_categories.map((cat, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 rounded bg-blue-500/15 border border-blue-500/30 text-blue-200">{cat}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedFinding.email_breaches && selectedFinding.email_breaches.length > 0 && (
                      <div className="col-span-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4">
                        <p className="text-xs text-indigo-300 uppercase tracking-widest mb-2">Email Breach List</p>
                        <ul className="space-y-1">
                          {selectedFinding.email_breaches.map((item, idx) => (
                            <li key={idx} className="text-xs text-slate-200 flex items-start gap-1.5"><span className="text-indigo-300">•</span>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedFinding.username_scan_results && selectedFinding.username_scan_results.length > 0 && (
                      <div className="col-span-2 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                        <p className="text-xs text-purple-300 uppercase tracking-widest mb-2">Username Scan Results</p>
                        <ul className="space-y-1">
                          {selectedFinding.username_scan_results.map((item, idx) => (
                            <li key={idx} className="text-xs text-slate-200 flex items-start gap-1.5"><span className="text-purple-300">•</span>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {selectedFinding.exif_metadata && (
                    <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> EXIF Metadata</h3>
                      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
                        {Object.entries(selectedFinding.exif_metadata).map(([key, val], i) => (
                          <div key={i} className={`flex justify-between px-4 py-2.5 text-xs ${i % 2 === 0 ? 'bg-slate-800/20' : ''}`}>
                            <span className="text-slate-500 font-medium">{key}</span>
                            <span className="text-slate-200 font-mono text-right ml-4">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedFinding.lat !== undefined || selectedFinding.crisis_zone) && (
                    <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5" /> Geospatial Intelligence</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedFinding.lat !== undefined && (
                          <div className="col-span-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                            <p className="text-xs text-emerald-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Coordinates</p>
                            <p className="text-sm text-emerald-300 font-mono">{Math.abs(selectedFinding.lat!).toFixed(4)}\u00b0{selectedFinding.lat! >= 0 ? 'N' : 'S'}, {Math.abs(selectedFinding.lon!).toFixed(4)}\u00b0{selectedFinding.lon! >= 0 ? 'E' : 'W'}</p>
                            {selectedFinding.resolved_location && <p className="text-xs text-slate-400 mt-1">{selectedFinding.resolved_location}</p>}
                          </div>
                        )}
                        {selectedFinding.crisis_zone && (
                          <div className="col-span-2 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                            <p className="text-xs text-red-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Crisis Zone Alert</p>
                            <p className="text-sm text-red-300 font-semibold">{selectedFinding.crisis_zone}</p>
                            <div className="flex items-center gap-3 mt-1">
                              {selectedFinding.crisis_severity && <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${selectedFinding.crisis_severity === 'critical' ? 'bg-red-500/20 text-red-300' : selectedFinding.crisis_severity === 'high' ? 'bg-orange-500/20 text-orange-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{selectedFinding.crisis_severity}</span>}
                              {selectedFinding.distance_km !== undefined && <span className="text-xs text-slate-400">{selectedFinding.distance_km} km from zone center</span>}
                            </div>
                          </div>
                        )}
                        {selectedFinding.callsign && (
                          <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-4">
                            <p className="text-xs text-sky-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Plane className="w-3 h-3" /> Callsign</p>
                            <p className="text-sm text-sky-300 font-mono font-bold">{selectedFinding.callsign}</p>
                            {selectedFinding.flight_origin && <p className="text-xs text-slate-400 mt-0.5">Origin: {selectedFinding.flight_origin}</p>}
                          </div>
                        )}
                        {selectedFinding.altitude_m !== undefined && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Altitude / Speed</p>
                            <p className="text-sm text-slate-200 font-mono">{(selectedFinding.altitude_m / 1000).toFixed(1)} km</p>
                            {selectedFinding.velocity_ms && <p className="text-xs text-slate-400 mt-0.5">{(selectedFinding.velocity_ms * 1.944).toFixed(0)} kn</p>}
                          </div>
                        )}
                        {selectedFinding.vessel_name && (
                          <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
                            <p className="text-xs text-teal-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Ship className="w-3 h-3" /> Vessel</p>
                            <p className="text-sm text-teal-300 font-bold">{selectedFinding.vessel_name}</p>
                            {selectedFinding.vessel_type && <p className="text-xs text-slate-400 mt-0.5">{selectedFinding.vessel_type}</p>}
                          </div>
                        )}
                        {selectedFinding.mmsi && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Anchor className="w-3 h-3" /> MMSI / Speed</p>
                            <p className="text-sm text-slate-200 font-mono">{selectedFinding.mmsi}</p>
                            {selectedFinding.speed_kn !== undefined && <p className="text-xs text-slate-400 mt-0.5">{selectedFinding.speed_kn} kn \u2192 {selectedFinding.destination || 'Unknown'}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedFinding.suite === 'neural' && (
                    <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> Neural AI Forensics</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedFinding.ai_confidence !== undefined && (
                          <div className="col-span-2 bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-2">
                              <p className="text-xs text-violet-400 uppercase tracking-widest flex items-center gap-1.5"><Wand2 className="w-3 h-3" /> AI Generation Confidence</p>
                              <span className="text-violet-300 font-black font-mono text-xl">{(selectedFinding.ai_confidence * 100).toFixed(0)}%</span>
                            </div>
                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${selectedFinding.ai_confidence * 100}%` }} /></div>
                          </div>
                        )}
                        {selectedFinding.generation_method && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Generation Method</p>
                            <p className="text-sm text-violet-300 font-medium">{selectedFinding.generation_method}</p>
                          </div>
                        )}
                        {selectedFinding.model_attribution && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Fingerprint className="w-3 h-3" /> Model Attribution</p>
                            <p className="text-sm text-purple-300 font-mono font-semibold">{selectedFinding.model_attribution}</p>
                          </div>
                        )}
                        {selectedFinding.perplexity_score !== undefined && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Perplexity Score</p>
                            <p className="text-sm text-slate-200 font-mono">{selectedFinding.perplexity_score.toFixed(1)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">lower = more AI-like</p>
                          </div>
                        )}
                        {selectedFinding.burstiness_score !== undefined && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Burstiness Score</p>
                            <p className="text-sm text-slate-200 font-mono">{selectedFinding.burstiness_score.toFixed(3)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">lower = more AI-like</p>
                          </div>
                        )}
                        {selectedFinding.watermark_status && (
                          <div className="col-span-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4">
                            <p className="text-xs text-indigo-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Fingerprint className="w-3 h-3" /> C2PA Watermark Status</p>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${selectedFinding.watermark_status === 'tampered' ? 'bg-red-500/20 text-red-300' : selectedFinding.watermark_status === 'absent' ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{selectedFinding.watermark_status}</span>
                              {selectedFinding.c2pa_issuer && <span className="text-xs text-slate-400">{selectedFinding.c2pa_issuer}</span>}
                            </div>
                          </div>
                        )}
                        {selectedFinding.frequency_anomaly !== undefined && (
                          <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-4">
                            <p className="text-xs text-pink-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Mic className="w-3 h-3" /> Frequency Anomaly</p>
                            <p className={`text-sm font-semibold ${selectedFinding.frequency_anomaly ? 'text-red-300' : 'text-emerald-300'}`}>{selectedFinding.frequency_anomaly ? 'Detected' : 'None'}</p>
                          </div>
                        )}
                        {selectedFinding.formant_deviation !== undefined && (
                          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1.5">Formant Deviation</p>
                            <p className="text-sm text-pink-300 font-mono font-bold">{selectedFinding.formant_deviation.toFixed(3)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">&gt;0.5 indicates synthesis</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedFinding.recommendations && (
                    <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Recommended Actions</h3>
                      <ul className="space-y-2">
                        {selectedFinding.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className="text-cyan-400 font-bold shrink-0 mt-0.5">\u2192</span> {rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedFinding.related_posts && selectedFinding.related_posts.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Associated Posts</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedFinding.related_posts.map((post, i) => (
                          <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-xs font-mono px-3 py-1 rounded-lg">{post}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {modalTab === 'narrative' && (
                <div className="space-y-5">
                  <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">What narratives is this entity pushing?</p>
                    <p className="text-xs text-slate-400 leading-relaxed">Cross-referenced against 142,000+ monitored posts to identify which crisis narratives this entity actively participates in or amplifies.</p>
                  </div>
                  {selectedFinding.narrative_footprint && selectedFinding.narrative_footprint.length > 0 ? (
                    selectedFinding.narrative_footprint.map((nf, i) => (
                      <div key={i} className={`border rounded-xl p-4 ${nf.exposure > 0.8 ? 'bg-rose-500/5 border-rose-500/30' : nf.exposure > 0.6 ? 'bg-amber-500/5 border-amber-500/20' : 'bg-slate-800/40 border-slate-700/50'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <p className={`text-sm font-bold ${nf.exposure > 0.8 ? 'text-rose-300' : nf.exposure > 0.6 ? 'text-amber-300' : 'text-slate-200'}`}>{nf.narrative}</p>
                          <span className={`text-xs font-black font-mono px-2 py-0.5 rounded-full ${nf.exposure > 0.8 ? 'bg-rose-500/20 text-rose-300' : nf.exposure > 0.6 ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700 text-slate-300'}`}>{(nf.exposure * 100).toFixed(0)}% exposure</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-3">
                          <div className={`h-full rounded-full ${nf.exposure > 0.8 ? 'bg-rose-500' : nf.exposure > 0.6 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${nf.exposure * 100}%` }} />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {nf.platforms.map(p => <span key={p} className="text-xs px-2 py-0.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 font-medium">{p}</span>)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-slate-500"><Network className="w-8 h-8 mx-auto mb-3 opacity-30" /><p className="text-sm">No narrative footprint data for this entity.</p></div>
                  )}
                  {selectedFinding.correlation_tags && (
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Cross-Signal Correlations</p>
                      <div className="space-y-2">
                        {selectedFinding.correlation_tags.map((tag, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-cyan-400 bg-cyan-500/5 border border-cyan-500/20 rounded-lg px-3 py-2">
                            <GitBranch className="w-3.5 h-3.5 shrink-0" /> {tag}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {modalTab === 'velocity' && (
                <div className="space-y-5">
                  {selectedFinding.velocity_projection ? (
                    <>
                      <div className={`border rounded-2xl p-6 text-center ${selectedFinding.velocity_projection.viral_probability > 0.7 ? 'bg-rose-500/5 border-rose-500/30' : 'bg-amber-500/5 border-amber-500/20'}`}>
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">If Activated \u2014 Projected 48h Impressions</p>
                        <p className={`text-5xl font-black font-mono ${selectedFinding.velocity_projection.viral_probability > 0.7 ? 'text-rose-400' : 'text-amber-400'}`}>
                          {selectedFinding.velocity_projection.impressions_48h >= 1e6 ? `${(selectedFinding.velocity_projection.impressions_48h / 1e6).toFixed(1)}M` : `${(selectedFinding.velocity_projection.impressions_48h / 1e3).toFixed(0)}K`}
                        </p>
                        <p className="text-slate-400 text-sm mt-1">estimated reach in 48 hours</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 text-center">
                          <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Viral Probability</p>
                          <p className={`text-3xl font-black font-mono ${selectedFinding.velocity_projection.viral_probability > 0.7 ? 'text-rose-400' : selectedFinding.velocity_projection.viral_probability > 0.5 ? 'text-amber-400' : 'text-emerald-400'}`}>{(selectedFinding.velocity_projection.viral_probability * 100).toFixed(0)}%</p>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden mt-2">
                            <div className={`h-full rounded-full ${selectedFinding.velocity_projection.viral_probability > 0.7 ? 'bg-rose-500' : 'bg-amber-500'}`} style={{ width: `${selectedFinding.velocity_projection.viral_probability * 100}%` }} />
                          </div>
                        </div>
                        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 text-center">
                          <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Projected Peak Hour</p>
                          <p className="text-xl font-black font-mono text-cyan-400">{new Date(selectedFinding.velocity_projection.peak_hour).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                          <p className="text-xs text-slate-500 mt-1">{new Date(selectedFinding.velocity_projection.peak_hour).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
                        </div>
                      </div>
                      <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${selectedFinding.velocity_projection.viral_probability > 0.7 ? 'bg-rose-500/5 border-rose-500/30 text-rose-300' : 'bg-amber-500/5 border-amber-500/20 text-amber-300'}`}>
                        <TrendingUp className="w-4 h-4 shrink-0 mt-0.5" />
                        <p>{selectedFinding.velocity_projection.viral_probability > 0.8 ? 'CRITICAL \u2014 This entity has high viral activation probability. Immediate containment action recommended before peak hour.' : selectedFinding.velocity_projection.viral_probability > 0.6 ? 'ELEVATED \u2014 Significant spread likely within 48 hours. Pre-emptive fact-check deployment advised.' : 'MODERATE \u2014 Monitor closely. Spread trajectory manageable with standard response protocols.'}</p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-slate-500"><TrendingUp className="w-8 h-8 mx-auto mb-3 opacity-30" /><p className="text-sm">Velocity projection data unavailable for this entity.</p></div>
                  )}
                </div>
              )}

              {modalTab === 'signals' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">Signals auto-pulled from the current scan that are correlated to this entity via shared infrastructure, actor overlap, or narrative co-occurrence.</p>
                  {selectedFinding.correlation_tags && selectedFinding.correlation_tags.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {selectedFinding.correlation_tags.map((tag, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-cyan-400 bg-cyan-500/5 border border-cyan-500/20 rounded-lg px-3 py-2 font-mono">
                          <GitBranch className="w-3.5 h-3.5 shrink-0" /> {tag}
                        </div>
                      ))}
                    </div>
                  )}
                  {findings.filter(f => f.entity !== selectedFinding.entity && selectedFinding.related_posts?.some(p => f.related_posts?.includes(p))).slice(0, 5).map((f, i) => (
                    <div key={i} className={`border rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:opacity-80 transition-all ${riskBg(f.risk_score)}`} onClick={() => { setSelectedFinding(f); setModalTab('overview'); setRiskExpanded(false); }}>
                      <div className={`p-2 rounded-lg shrink-0 ${typeStyle(f.type)}`}>{typeIcon(f.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono font-bold text-white truncate">{f.entity}</p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{f.impact_context ?? f.reputation}</p>
                      </div>
                      <span className={`text-sm font-black font-mono shrink-0 ${riskColor(f.risk_score)}`}>{(f.risk_score * 100).toFixed(0)}%</span>
                      <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                    </div>
                  ))}
                  {findings.filter(f => f.entity !== selectedFinding.entity && selectedFinding.related_posts?.some(p => f.related_posts?.includes(p))).length === 0 && (
                    <div className="text-center py-10 text-slate-500"><Layers className="w-7 h-7 mx-auto mb-3 opacity-30" /><p className="text-sm">No directly correlated signals found in current scan.</p></div>
                  )}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-8 py-5 border-t border-slate-800 flex items-center justify-between gap-3 shrink-0 bg-slate-950/50">
              <span className="text-xs text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(selectedFinding.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} IST</span>
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all">
                  <Share2 className="w-3.5 h-3.5" /> Share Secure Link
                </button>
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-violet-300 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 rounded-xl transition-all">
                  <CheckCircle className="w-3.5 h-3.5" /> Add to Active Crisis Workspace
                </button>
                <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 rounded-xl transition-all shadow-lg shadow-cyan-900/30">
                  <Download className="w-3.5 h-3.5" /> Export Client Briefing PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelligenceCenter;
